import { db } from '../lib/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  doc, 
  getDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { Match, Prediction, User, UserRanking, OverallRanking } from '../types';

export const quinielaService = {
  // Obtener partidos activos agrupados por fase
  async getActiveMatches(): Promise<Record<string, Match[]>> {
    const matchesRef = collection(db, 'matches');
    const q = query(matchesRef, where('activo', '==', true));
    const querySnapshot = await getDocs(q);
    
    const matches = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Match[];

    // Agrupar por fase
    return matches.reduce((acc, match) => {
      if (!acc[match.phase]) {
        acc[match.phase] = [];
      }
      acc[match.phase].push(match);
      return acc;
    }, {} as Record<string, Match[]>);
  },

  // Obtener predicciones del usuario
  async getUserPredictions(userId: string): Promise<Prediction[]> {
    const predictionsRef = collection(db, 'predictions');
    const q = query(predictionsRef, where('user_id', '==', userId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Prediction[];
  },

  // Enviar predicciones
  async submitPredictions(userId: string, predictions: Record<string, { score_a: number; score_b: number }>) {
    const batch = writeBatch(db);
    
    Object.entries(predictions).forEach(([matchId, scores]) => {
      const predictionRef = doc(collection(db, 'predictions'));
      batch.set(predictionRef, {
        user_id: userId,
        match_id: matchId,
        predicted_score_a: scores.score_a,
        predicted_score_b: scores.score_b,
        activo: true,
        created_at: new Date(),
        updated_at: new Date()
      });
    });

    await batch.commit();
  },

  // Calcular ranking
  async calculateRanking(phase?: string): Promise<{ rankings: UserRanking[]; overallRankings: OverallRanking[] }> {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as User[];

    // Obtener todos los partidos y predicciones
    const matchesRef = collection(db, 'matches');
    const predictionsRef = collection(db, 'predictions');
    
    const [matchesSnapshot, predictionsSnapshot] = await Promise.all([
      getDocs(matchesRef),
      getDocs(predictionsRef)
    ]);

    const matches = matchesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Match[];

    const predictions = predictionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Prediction[];

    // Calcular puntos por usuario
    const userPoints: Record<string, number> = {};
    const userPhasePoints: Record<string, Record<string, number>> = {};

    // Inicializar puntos
    users.forEach(user => {
      userPoints[user.id] = 0;
      userPhasePoints[user.id] = {};
    });

    // Calcular puntos por predicción
    predictions.forEach(prediction => {
      const match = matches.find(m => m.id === prediction.match_id);
      if (!match || match.score_a === null || match.score_b === null) return;

      let points = 0;
      if (prediction.predicted_score_a === match.score_a && 
          prediction.predicted_score_b === match.score_b) {
        points = 3; // Resultado exacto
      } else if ((prediction.predicted_score_a > prediction.predicted_score_b && match.score_a > match.score_b) ||
                 (prediction.predicted_score_a < prediction.predicted_score_b && match.score_a < match.score_b) ||
                 (prediction.predicted_score_a === prediction.predicted_score_b && match.score_a === match.score_b)) {
        points = 1; // Resultado correcto
      }

      userPoints[prediction.user_id] += points;
      
      if (!userPhasePoints[prediction.user_id][match.phase]) {
        userPhasePoints[prediction.user_id][match.phase] = 0;
      }
      userPhasePoints[prediction.user_id][match.phase] += points;
    });

    // Generar rankings
    const rankings: UserRanking[] = [];
    const overallRankings: OverallRanking[] = [];

    // Rankings por fase si se especifica una
    if (phase) {
      users.forEach(user => {
        if (userPhasePoints[user.id][phase]) {
          rankings.push({
            name: user.displayName || 'Usuario desconocido',
            points: userPhasePoints[user.id][phase]
          });
        }
      });
    }

    // Ranking general
    users.forEach(user => {
      overallRankings.push({
        name: user.displayName || 'Usuario desconocido',
        total_points: userPoints[user.id]
      });
    });

    // Ordenar rankings
    rankings.sort((a, b) => b.points - a.points);
    overallRankings.sort((a, b) => b.total_points - a.total_points);

    return { rankings, overallRankings };
  }
}; 